import { useCallback, useMemo } from "react"
import { useParams } from "next/navigation"

import type { MetricFilterAdapter } from "@/metrics/runtime-adapters"

import {
  metricValueFilterEquals,
  type MetricValueFilter,
} from "@/lib/metricValueFilters"
import { useLocalStorage } from "@/hooks/useLocalStorage"
import type { RunRowLike } from "@/types/RunRow"

import {
  filterRunRows,
  type RunResultsFilterState,
} from "../utils/filterRunRows"

export interface RunResultsFilterController extends RunResultsFilterState {
  filteredRunRows: RunRowLike[]
  isOutliersEnabled: boolean
  outlierMetricIds: string[] | null
  hasActiveFilters: boolean
  addMetricFilters: (filters: MetricValueFilter[]) => void
  clearAllFilters: () => void
  clearMetricFilters: () => void
  removeMetricFilters: (filters: MetricValueFilter[]) => void
  setMetricFilters: (filters: MetricValueFilter[]) => void
  setMutationFilterIds: (ids: string[]) => void
  setOutliersEnabled: (enabled: boolean) => void
  setOutlierMetricIds: (ids: string[] | null) => void
  setStatusFilterStatuses: (statuses: string[]) => void
}

interface UseRunResultsFiltersParams {
  metricRecordsById: Record<string, MetricFilterAdapter>
  runRows: RunRowLike[]
}

export function useRunResultsFilters({
  metricRecordsById,
  runRows,
}: UseRunResultsFiltersParams): RunResultsFilterController {
  const params = useParams()
  const runIdParam = params?.run_id
  const runId =
    typeof runIdParam === "string"
      ? runIdParam
      : Array.isArray(runIdParam) && typeof runIdParam[0] === "string"
        ? runIdParam[0]
        : ""

  const [metricFilters, setMetricFilters] = useLocalStorage<
    MetricValueFilter[]
  >(`run-filters-${runId}`, [])
  const [mutationFilterIds, setMutationFilterIds] = useLocalStorage<string[]>(
    `run-mutation-filters-${runId}`,
    [],
  )
  const [statusFilterStatuses, setStatusFilterStatuses] = useLocalStorage<
    string[]
  >(`run-status-filters-${runId}`, [])
  const [isOutliersEnabled, setOutliersEnabled] = useLocalStorage<boolean>(
    runId ? `run-outliers-${runId}` : "run-results-outliers-unavailable",
    false,
  )
  const [outlierMetricIds, setOutlierMetricIds] = useLocalStorage<
    string[] | null
  >(
    runId ? `run-outlier-metrics-${runId}` : "run-outlier-metrics-unavailable",
    null,
  )

  const filteredRunRows = useMemo(
    () =>
      filterRunRows({
        runRows,
        metricRecordsById,
        filters: {
          metricFilters,
          mutationFilterIds,
          statusFilterStatuses,
        },
      }),
    [
      metricFilters,
      metricRecordsById,
      mutationFilterIds,
      runRows,
      statusFilterStatuses,
    ],
  )

  const addMetricFilters = useCallback(
    (filtersToAdd: MetricValueFilter[]) => {
      const dedupedFilters = filtersToAdd.filter(
        (filterToAdd) =>
          !metricFilters.some((existingFilter) =>
            metricValueFilterEquals(existingFilter, filterToAdd),
          ),
      )

      if (dedupedFilters.length === 0) {
        return
      }

      setMetricFilters([...metricFilters, ...dedupedFilters])
    },
    [metricFilters, setMetricFilters],
  )

  const removeMetricFilters = useCallback(
    (filtersToRemove: MetricValueFilter[]) => {
      setMetricFilters(
        metricFilters.filter(
          (existingFilter) =>
            !filtersToRemove.some((filterToRemove) =>
              metricValueFilterEquals(filterToRemove, existingFilter),
            ),
        ),
      )
    },
    [metricFilters, setMetricFilters],
  )

  const clearMetricFilters = useCallback(() => {
    setMetricFilters([])
  }, [setMetricFilters])

  const clearAllFilters = useCallback(() => {
    setMetricFilters([])
    setMutationFilterIds([])
    setStatusFilterStatuses([])
    setOutliersEnabled(false)
    setOutlierMetricIds(null)
  }, [
    setMetricFilters,
    setMutationFilterIds,
    setOutliersEnabled,
    setOutlierMetricIds,
    setStatusFilterStatuses,
  ])

  return {
    filteredRunRows,
    isOutliersEnabled,
    outlierMetricIds,
    metricFilters,
    mutationFilterIds,
    statusFilterStatuses,
    hasActiveFilters:
      metricFilters.length > 0 ||
      mutationFilterIds.length > 0 ||
      statusFilterStatuses.length > 0 ||
      isOutliersEnabled,
    addMetricFilters,
    clearAllFilters,
    clearMetricFilters,
    removeMetricFilters,
    setMetricFilters,
    setMutationFilterIds,
    setOutliersEnabled,
    setOutlierMetricIds,
    setStatusFilterStatuses,
  }
}
